<template>
  <div
    class="hps"
    :class="['hps--' + variant, { 'hps--open': isOpen }]"
    data-test="header-patient-search"
  >
    <i v-if="variant === 'touch'" class="fa fa-search hps-icon" aria-hidden="true"></i>

    <multiselect
      ref="multiselect"
      v-model="selected"
      :options="visibleOptions"
      :multiple="false"
      :searchable="true"
      :internal-search="false"
      :clear-on-select="true"
      :close-on-select="true"
      :show-labels="false"
      :custom-label="patientLabel"
      track-by="PatNum"
      open-direction="bottom"
      :max-height="320"
      :placeholder="placeholderText"
      @search-change="onSearchChange"
      @select="onSelect"
      @open="onOpen"
      @close="onClose"
    >
      <template slot="beforeList">
        <li v-if="showRecentHeading" class="hps-heading" data-test="header-patient-search-recent-heading">
          Recently viewed
        </li>
      </template>

      <template slot="option" slot-scope="props">
        <div class="hps-option">
          <div class="hps-option__text">
            <span class="hps-option__name option__title">{{ patientLabel(props.option) }}</span>
            <span v-if="dob(props.option)" class="hps-option__dob option__small">{{ dob(props.option) }}</span>
          </div>
          <!-- @click.stop beats the option span's own @click.stop="select(option)" (the child
               listener runs first), so the camera never ALSO navigates as a plain select.
               @mousedown.prevent keeps the search input focused, like the list itself does. -->
          <button
            v-if="photoShortcut"
            type="button"
            class="hps-photo"
            data-test="header-patient-search-photo"
            :aria-label="'Take a photo of ' + patientLabel(props.option)"
            @mousedown.prevent.stop
            @click.stop.prevent="onPhoto(props.option)"
          >
            <i class="fa fa-camera" aria-hidden="true"></i>
          </button>
        </div>
      </template>

      <template slot="noOptions">
        <span class="hps-hint" data-test="header-patient-search-hint">Type a patient's name</span>
      </template>

      <template slot="noResult">
        <span class="hps-noresult">
          No patient matches.
          <button
            type="button"
            class="hps-refresh"
            data-test="header-patient-search-refresh"
            :disabled="refreshing"
            @mousedown.prevent.stop
            @click.stop.prevent="$emit('refresh')"
          >
            <i class="fa" :class="refreshing ? 'fa-spinner fa-spin' : 'fa-refresh'" aria-hidden="true"></i>
            {{ refreshing ? 'Refreshing…' : 'Refresh patient list' }}
          </button>
        </span>
      </template>
    </multiselect>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import {
  PATIENT_SEARCH_LIMIT,
  buildPatientSearchIndex,
  formatDob,
  searchPatients,
} from '@/utils/patientSearch';

/**
 * The header's patient search — ONE component for every header layout.
 *
 * Header.vue used to paste the same <multiselect> into two places (the ☰ dropdown and the
 * desktop nav), bound to one v-model with track-by="LName". That had two costs: every edit had
 * to be made twice, and a patient sharing the previous patient's surname could not be opened —
 * vue-multiselect saw "already selected" and DESELECTED instead of emitting select
 * (multiselectMixin.js select()). Here:
 *
 *   - track-by is PatNum, and the value is cleared after every select, so every tap navigates
 *     (the same patient twice, or John Smith then Jane Smith).
 *   - Matching is utils/patientSearch.js — patient NAMES in any order — not the widget's
 *     substring filter. Birthdate and phone are deliberately not searchable (2026-09-13); the
 *     birthdate is shown under each name only to tell same-named patients apart.
 *   - An empty query lists Recently viewed (PatNums from the parent, resolved against the
 *     masked roster here, so no name is ever read from storage).
 *   - `touch` variant: a pill field sized for the tablet header bar (14px / 36px at 768px and up)
 *     and for phones (16px / 44px below 768px — iOS Safari zooms the page when a focused input
 *     is under 16px).
 *
 * No Vuex, no router: the parent owns data and navigation, which is what lets
 * tests/component/HeaderPatientSearch.spec.js mount it.
 *
 * Emits: select(patient), photo(patient), refresh, open, close.
 */
export default {
  name: 'HeaderPatientSearch',
  components: { Multiselect },

  props: {
    patients: { type: Array, default: () => [] },
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
    variant: {
      type: String,
      default: 'compact',
      validator: (v) => ['compact', 'touch'].indexOf(v) > -1,
    },
    recentPatNums: { type: Array, default: () => [] },
    photoShortcut: { type: Boolean, default: false },
    refreshing: { type: Boolean, default: false },
  },

  data() {
    return {
      selected: null,
      query: '',
      isOpen: false,
    };
  },

  computed: {
    searchIndex() {
      return buildPatientSearchIndex(this.patients);
    },

    patientsByPatNum() {
      const map = new Map();
      (this.patients || []).forEach((p) => {
        if (p && p.PatNum != null) map.set(String(p.PatNum), p);
      });
      return map;
    },

    // A recent PatNum that is no longer in the roster (archived, other status) is skipped.
    recentPatients() {
      return (this.recentPatNums || [])
        .map((n) => this.patientsByPatNum.get(String(n)))
        .filter(Boolean);
    },

    hasQuery() {
      return this.query.trim().length > 0;
    },

    visibleOptions() {
      return this.hasQuery
        ? searchPatients(this.searchIndex, this.query, PATIENT_SEARCH_LIMIT)
        : this.recentPatients;
    },

    showRecentHeading() {
      return !this.hasQuery && this.recentPatients.length > 0;
    },

    placeholderText() {
      return this.variant === 'touch' ? 'Search patients' : 'Search Patients';
    },
  },

  methods: {
    patientLabel(patient) {
      if (!patient) return '';
      return `${patient.FName || ''} ${patient.LName || ''}`.trim();
    },

    dob(patient) {
      return patient ? formatDob(patient.Birthdate, this.dateFormat) : '';
    },

    onSearchChange(query) {
      this.query = query || '';
    },

    onSelect(patient) {
      this.$emit('select', patient);
      // Clear AFTER vue-multiselect has committed the value, so the next tap on any patient —
      // this one included — is a fresh select, never a deselect.
      this.$nextTick(() => {
        this.selected = null;
      });
    },

    onPhoto(patient) {
      this.blur();
      this.$emit('photo', patient);
    },

    onOpen() {
      this.isOpen = true;
      this.$emit('open');
    },

    onClose() {
      this.isOpen = false;
      this.$emit('close');
    },

    /**
     * Focus the search input SYNCHRONOUSLY. vue-multiselect's own activate() focuses in
     * $nextTick, which is outside the tap on iOS — Safari then opens the list without raising
     * the keyboard. The input's @focus handler opens the list itself.
     */
    focus() {
      const ms = this.$refs.multiselect;
      const input = ms && ms.$refs ? ms.$refs.search : null;
      if (input && typeof input.focus === 'function') input.focus();
    },

    blur() {
      const ms = this.$refs.multiselect;
      if (ms && typeof ms.deactivate === 'function') ms.deactivate();
    },
  },
};
</script>

<style lang="scss" scoped>
// Everything vue-multiselect renders itself carries ITS scope, not ours, so those rules pierce
// with ::v-deep — always under .hps, so nothing can leak to another page's multiselect. Slot
// content (the option row, heading, hint) is compiled here and takes plain scoped rules.

.hps {
  position: relative;
  width: 100%;
}

.hps-heading {
  list-style: none;
  padding: 8px 12px 4px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.hps-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.hps-option__text {
  flex: 1 1 auto;
  min-width: 0;
}

.hps-option__dob {
  margin-left: 0.5rem;
  font-size: 10px;
}

.hps-photo {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid #cfe3dc;
  border-radius: 50%;
  background: #fff;
  color: #0f7259;
  line-height: 1;
  cursor: pointer;
}

.hps-hint,
.hps-noresult {
  white-space: normal;
  color: #64748b;
}

.hps-refresh {
  padding: 0 0 0 4px;
  border: 0;
  background: transparent;
  color: #0f7259;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

// ── Touch variant ─────────────────────────────────────────────────────────────────────────
// The rules in this block are the PHONE BASE (<768px): 16px text, 44px field and rows. The
// tablet/laptop sizes (14px, 36px) are the @media (min-width: 768px) override further down.
//
// ⚠️ The !important flags are deliberate and contained (they sit under the scoped
// .hps--touch[data-v] ancestor, so they reach nothing but this field). An UNSCOPED page
// stylesheet already loaded app-wide (src/pages/Settings/Settings.scss) sets
//   .multiselect .multiselect__tags  { padding / border / radius / min-height / background … !important }
//   .multiselect .multiselect__input { font-size: 0.95rem !important }   (= 13.3px)
// on every multiselect in the app. Without these flags the field measured 8px radius, 13.3px
// text (iOS focus zoom) and a placeholder under the search icon (Cypress, 2026-09-13).
.hps--touch {
  .hps-icon {
    position: absolute;
    left: 14px;
    top: 22px;
    transform: translateY(-50%);
    z-index: 51; // above .multiselect--active (50)
    color: #8a94a3;
    font-size: 15px;
    pointer-events: none;
    transition: color 0.15s ease;
  }

  ::v-deep .multiselect {
    min-height: 44px;
  }

  // At rest: a quiet light-grey pill. Open: white with a thin teal focus ring (below).
  ::v-deep .multiselect__tags {
    min-height: 44px !important;
    padding: 12px 16px 0 38px !important;
    border: 1px solid transparent !important;
    border-radius: 22px !important;
    background: #f4f6f8 !important;
    transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  }

  ::v-deep .multiselect--active .multiselect__tags {
    border-radius: 22px 22px 0 0 !important;
  }

  ::v-deep .multiselect__select {
    display: none;
  }

  // ⛔ PHONES: 16px, not the global 12px / 13.3px — iOS Safari zooms the whole page when a
  // focused input's font-size is under 16px. Only the ≥768px override below may go smaller.
  ::v-deep .multiselect__input,
  ::v-deep .multiselect__single,
  ::v-deep .multiselect__placeholder {
    min-height: 20px;
    margin-bottom: 11px;
    padding: 0;
    background: transparent;
    font-size: 16px !important;
    line-height: 20px;
  }

  ::v-deep .multiselect__input::placeholder {
    color: #8a94a3;
  }

  // One line, always: a wrapped placeholder pushes the field past the fixed 60px header.
  ::v-deep .multiselect__placeholder {
    padding-top: 0;
    color: #8a94a3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ::v-deep .multiselect__content-wrapper {
    border: 1px solid #d5dbe3;
    border-top: 1px solid #eef1f4;
    border-radius: 0 0 16px 16px;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.1);
  }

  ::v-deep .multiselect__option {
    display: flex;
    align-items: center;
    min-height: 44px;
    padding: 8px 12px;
    font-size: 16px;
    line-height: 1.25;
    white-space: normal;
    color: #1e293b;
  }

  // Soft teal instead of vue-multiselect's solid #41b883 row with white text.
  ::v-deep .multiselect__option--highlight {
    background: #eef7f4;
    color: #1e293b;
  }

  .hps-option__text {
    display: flex;
    flex-direction: column;
  }

  .hps-option__name {
    font-weight: 500;
  }

  .hps-option__dob {
    margin-left: 0;
    font-size: 13px;
    color: #64748b;
  }

  .hps-photo {
    width: 44px;
    height: 44px;
    font-size: 18px;
  }

  // Open: white field, teal border and focus ring, teal icon.
  &.hps--open {
    .hps-icon {
      color: #0f7259;
    }

    ::v-deep .multiselect__tags {
      border-color: #0f7259 !important;
      background: #fff !important;
      box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.14);
    }
  }
}

// ── Tablet / laptop (768px and up): smaller, matching the 14px header typography ──────────
// Same 768 edge as Header.vue's PHONE_QUERY. iPadOS Safari does not zoom on a 14px input the way
// iPhone Safari does — to be confirmed on a real iPad; phones keep the 16px base above.
@media (min-width: 768px) {
  .hps--touch {
    .hps-icon {
      left: 13px;
      top: 18px;
      font-size: 13px;
    }

    ::v-deep .multiselect {
      min-height: 36px;
    }

    // 1 + 7 + 20 + 7 + 1 = 36px. The explicit line-height / font-size on the field and
    // `vertical-align: top` on its text stop the inline-block placeholder from sitting on the
    // field's default-line-height baseline, which added ~6px (measured 42px, Cypress 2026-09-13).
    ::v-deep .multiselect__tags {
      min-height: 36px !important;
      padding: 7px 14px 0 34px !important;
      border-radius: 18px !important;
      font-size: 14px !important;
      line-height: 20px;
    }

    ::v-deep .multiselect--active .multiselect__tags {
      border-radius: 18px 18px 0 0 !important;
    }

    ::v-deep .multiselect__input,
    ::v-deep .multiselect__single,
    ::v-deep .multiselect__placeholder {
      margin-bottom: 7px;
      font-size: 14px !important;
      vertical-align: top;
    }

    ::v-deep .multiselect__content-wrapper {
      min-width: 320px;
      border-radius: 0 0 12px 12px;
    }

    ::v-deep .multiselect__option {
      min-height: 40px;
      padding: 6px 12px;
      font-size: 14px;
    }

    .hps-heading {
      padding: 6px 12px 2px;
      font-size: 11px;
    }

    .hps-option__dob {
      font-size: 12px;
    }

    .hps-photo {
      width: 36px;
      height: 36px;
      font-size: 16px;
    }
  }
}
</style>
